import { DescriptionBox } from "@jugl-web/domain-resources/common/components/DescriptionBox";
import { useSkipFutureAlerts } from "@jugl-web/domain-resources/common/hooks/useSkipFutureAlerts";
import {
  MAX_TICKET_DESCRIPTION_LENGTH,
  MAX_TICKET_TITLE_LENGTH,
} from "@jugl-web/domain-resources/tickets/consts";
import {
  TicketStatus,
  UpdateTicketPayload,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models/TicketDetails";
import { TitleBox } from "@jugl-web/ui-kit";
import { useToast } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { RESOLVE_TICKET_ALERT_ID } from "@jugl-web/utils/storage";
import { ResolveTicketDialog } from "@web-src/modules/client-support/components/ResolveTicketDialog";
import { UpdateTicketContactDialog } from "@web-src/modules/client-support/components/UpdateTicketContactDialog";
import { useUpdateTicketContactDialog } from "@web-src/modules/client-support/hooks/useUpdateTicketContactDialog";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useMemo } from "react";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { AssigneesDetailItem } from "../../../AssigneesDetailItem";
import { ContactDetailItem } from "../../../ContactDetailItem";
import { DueToDetailItem } from "../../../DueToDetailItem";
import { PriorityDetailItem } from "../../../PriorityDetailItem";
import { StatusDetailItem } from "../../../StatusDetailItem";
import { OpenedAtDetailItem } from "../OpenedAtDetailItem";
import { OpenedByDetailItem } from "../OpenedByDetailItem";

interface DetailsTabContentProps {
  ticket: TicketDetailsModel;
}

export const DetailsTabContent: FC<DetailsTabContentProps> = ({ ticket }) => {
  const { entityId } = useEntitySelectedProvider();

  const { ticketsApi } = useRestApiProvider();
  const { ticketMutations$ } = useClientSupportPageContext();

  const { toast } = useToast();

  const { areFutureAlertsSkipped, onSkipFutureAlertsChange } =
    useSkipFutureAlerts();

  const [updateTicketMutation] = ticketsApi.useUpdateTicketMutation();

  const updateTicket = useCallback(
    async (data: UpdateTicketPayload) => {
      try {
        const updatedTicket = await updateTicketMutation({
          entityId,
          ticketId: ticket.id,
          data,
        }).unwrap();

        ticketMutations$.next({ type: "updated", ticket: updatedTicket });
        return updatedTicket;
      } catch {
        toast("Failed to update ticket", { variant: "error" });
        return undefined;
      }
    },
    [updateTicketMutation, entityId, ticket.id, ticketMutations$, toast]
  );

  const updateTicketContact = useCallback(
    async (contactId: string) => {
      await updateTicket({ contact_id: contactId });
      toast("Ticket customer has been updated");
    },
    [toast, updateTicket]
  );

  const { updateTicketContactDialogProps, requestUpdateContact } =
    useUpdateTicketContactDialog({
      currentContact: ticket.contact,
      onContactUpdate: updateTicketContact,
    });

  const resolveTicketConfirmationDialogState = useConfirmationDialogState();

  const handleStatusChange = useCallback(
    async (status: TicketStatus) => {
      if (status !== "resolved") {
        updateTicket({ status });
        return;
      }

      if (areFutureAlertsSkipped(RESOLVE_TICKET_ALERT_ID)) {
        const updatedTicket = await updateTicket({ status: "resolved" });

        if (updatedTicket) {
          toast("Ticket has been resolved");
        }

        return;
      }

      resolveTicketConfirmationDialogState.open();
    },
    [
      areFutureAlertsSkipped,
      resolveTicketConfirmationDialogState,
      toast,
      updateTicket,
    ]
  );

  const handleConfirmResolveTicket = useCallback(
    async (shouldSkipFutureAlerts: boolean) => {
      const updatedTicket = await updateTicket({ status: "resolved" });

      if (!updatedTicket) {
        return;
      }

      if (shouldSkipFutureAlerts) {
        onSkipFutureAlertsChange(RESOLVE_TICKET_ALERT_ID, true);
      }

      toast("Ticket has been resolved");
      resolveTicketConfirmationDialogState.close();
    },
    [
      updateTicket,
      toast,
      resolveTicketConfirmationDialogState,
      onSkipFutureAlertsChange,
    ]
  );

  const ticketAssigneeIds = useMemo(
    () => ticket.assignees.map((a) => a.user_id),
    [ticket.assignees]
  );

  return (
    <div>
      <div className="px-8 pt-8 pb-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs leading-[14px] font-normal text-grey-700">
            #{ticket.unique_id}
          </span>
          <TitleBox
            isEditable
            title={ticket.title}
            placeholder="Enter ticket topic"
            emptyPlaceholder="(no topic)"
            maxLength={MAX_TICKET_TITLE_LENGTH}
            onChange={async (title) => updateTicket({ title })}
            validator={(title) => title.trim().length > 0}
          />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-2 p-8">
        {/* Contact */}
        <ContactDetailItem
          contact={ticket.contact}
          onChange={requestUpdateContact}
        />
        {/* Assignees */}
        <AssigneesDetailItem
          assigneeIds={ticketAssigneeIds}
          onChange={(assigneeIds) => {
            updateTicket({ assignees: assigneeIds });
          }}
        />
        {/* Status */}
        <StatusDetailItem
          status={ticket.status}
          onChange={handleStatusChange}
        />
        {/* Priority */}
        <PriorityDetailItem
          priority={ticket.priority}
          onChange={(priority) => {
            updateTicket({ priority });
          }}
        />
        {/* Due To */}
        <DueToDetailItem
          dueDate={ticket.due_at ? new Date(ticket.due_at) : null}
          status={ticket.status}
          onChange={(dueDate) => {
            updateTicket({ due_at: dueDate ? dueDate.toISOString() : null });
          }}
        />
        {/* Created by */}
        <OpenedByDetailItem createdBy={ticket.created_by} />
        {/* Created at */}
        <OpenedAtDetailItem insertedAt={ticket.inserted_at} />
      </div>

      <Separator />

      <div className="p-8">
        {/* Description */}
        <div className="mb-4 flex flex-col gap-2 px-3">
          <span className="font-primary text-xs leading-[1.4] tracking-[0.12px] text-dark-700">
            Description:
          </span>
          <DescriptionBox
            isEditable
            description={ticket.desc || ""}
            classes={{ descriptionText: "py-2" }}
            onChange={(description) => {
              updateTicket({ desc: description });
            }}
            maxTextLength={MAX_TICKET_DESCRIPTION_LENGTH}
          />
        </div>
      </div>
      <UpdateTicketContactDialog {...updateTicketContactDialogProps} />
      <ResolveTicketDialog
        isOpen={resolveTicketConfirmationDialogState.isOpen}
        onClose={resolveTicketConfirmationDialogState.close}
        onConfirm={(options) =>
          handleConfirmResolveTicket(options.skipFutureAlerts ?? false)
        }
        ticketUniqueId={ticket.unique_id}
      />
    </div>
  );
};

const Separator = () => <div className="h-px w-full bg-grey-200" />;
